import { useState, useEffect } from 'react';
import { SUBMISSION_WINDOW } from '../constants/submissionWindow';

interface SubmissionWindowState {
  /** Whether the submission window is currently open */
  isWindowOpen: boolean;
  /** Window open date (represented as wall-clock values in the configured timezone) */
  windowStart: Date;
  /** Window close date (represented as wall-clock values in the configured timezone) */
  windowEnd: Date;
  /** IANA timezone identifier used for the window */
  timeZoneId: string;
}

/**
 * Extracts wall-clock date parts for the given timezone and returns
 * them packed into a plain Date (used only for comparison, not as a
 * real instant).  Both "now" and the window bounds go through this
 * same function so the comparison is always apples-to-apples.
 */
function toWallClock(date: Date, timeZoneId: string): Date {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timeZoneId,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes): number => {
    const value = parts.find((p) => p.type === type)?.value ?? '0';
    return parseInt(value, 10);
  };

  return new Date(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second')
  );
}

/**
 * Builds wall-clock Date objects for the window boundaries.
 * These are "fake" Dates holding numeric values (year, month, day, etc.)
 * in the same coordinate space as the output of toWallClock, so direct
 * comparison works correctly regardless of the browser's local timezone.
 */
function getWindowBounds(year: number): { start: Date; end: Date } {
  const { START_MONTH, START_DAY, END_MONTH, END_DAY, END_HOUR, END_MINUTE } =
    SUBMISSION_WINDOW;

  return {
    start: new Date(year, START_MONTH - 1, START_DAY, 0, 0, 0),
    end: new Date(year, END_MONTH - 1, END_DAY, END_HOUR, END_MINUTE, 0),
  };
}

function checkWindowOpen(timeZoneId: string): SubmissionWindowState {
  const now = new Date();
  const localNow = toWallClock(now, timeZoneId);
  const { start, end } = getWindowBounds(localNow.getFullYear());

  // Keep the window always open. Left the logic in case we used in the future.
  // Flip this to `false` (or wire it to a real config flag) to re-enable gating.
  const bypassWindow = true;
  const isWithinWindow  = bypassWindow || (localNow >= start && localNow < end);
  return {
    isWindowOpen: bypassWindow || isWithinWindow,  
    windowStart: start,
    windowEnd: end,
    timeZoneId,
  };
}

/**
 * React hook that reports whether the submission window is currently open.
 *
 * In development mode (`import.meta.env.DEV`) the window is always open
 * so developers can work on gated pages without waiting for the real window.
 *
 * Rechecks every minute so the UI updates automatically at window boundaries.
 */
export function useSubmissionWindow(): SubmissionWindowState {
  const timeZoneId = SUBMISSION_WINDOW.TIMEZONE_ID;

  const [state, setState] = useState<SubmissionWindowState>(() =>
    checkWindowOpen(timeZoneId)
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setState(checkWindowOpen(timeZoneId));
    }, 60_000);

    return () => clearInterval(interval);
  }, [timeZoneId]);

  return state;
}
